import { useEffect, useState } from "react";

const THEME_STORAGE_KEY = "jcfitness-theme-v2";

type ThemeMode = "light" | "dark";

const listeners = new Set<(value: ThemeMode) => void>();

function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  return window.localStorage.getItem(THEME_STORAGE_KEY) === "light" ? "light" : "dark";
}

let currentTheme: ThemeMode = readStoredTheme();

function applyTheme(value: ThemeMode) {
  if (typeof document !== "undefined") {
    document.body.classList.toggle("theme-dark", value === "dark");
  }
  if (typeof window !== "undefined") {
    window.localStorage.setItem(THEME_STORAGE_KEY, value);
  }
}

function setGlobalTheme(value: ThemeMode) {
  if (value === currentTheme) return;
  currentTheme = value;
  applyTheme(value);
  listeners.forEach((listener) => listener(value));
}

// Apply on first load so body class matches stored value before any component mounts
if (typeof window !== "undefined") {
  applyTheme(currentTheme);
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_STORAGE_KEY && (event.newValue === "dark" || event.newValue === "light")) {
      setGlobalTheme(event.newValue);
    }
  });
}

export function useThemeMode() {
  const [theme, setTheme] = useState<ThemeMode>(currentTheme);

  useEffect(() => {
    listeners.add(setTheme);
    if (theme !== currentTheme) setTheme(currentTheme);
    return () => {
      listeners.delete(setTheme);
    };
  }, [theme]);

  return {
    theme,
    toggleTheme() {
      setGlobalTheme(currentTheme === "dark" ? "light" : "dark");
    },
    setTheme(value: ThemeMode) {
      setGlobalTheme(value);
    },
  };
}
